/* eslint-disable react-native/no-inline-styles */
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { theme } from '../theme/theme';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik/dist';
import * as Yup from 'yup';
import { doPut } from '../services/http';
import { errorHandler } from '../services/Utils';
import { hideLoader, setOTPInfo, showLoader, showToast } from '../store/info.slice';
import AggrLogo from '../components/AggrLogo';

const InputRoundness = 6;
// Regex for password strength
const passwordValidationRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;

// Validation schema using Yup
const validationSchema = Yup.object().shape({
    password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .matches(passwordValidationRegex, 'Password must contain at least one uppercase letter, one number, and one special character')
        .required('Password is required'),
});


function ResetPassword({ navigation }) {
    const dispatch = useDispatch();
    const otpInfo = useSelector((state) => state.info?.otpInfo);
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    // Function to calculate password strength and return percentage
    const getPasswordStrength = (password) => {
        let strength = 0;

        // Check length (minimum 6 characters)
        if (password.length >= 6) { strength += 20; }

        // Check for uppercase letters
        if (password.match(/[A-Z]/)) { strength += 20; }

        // Check for numbers
        if (password.match(/\d/)) { strength += 20; }

        // Check for special characters
        if (password.match(/[@$!%*?&]/)) { strength += 20; }

        // Check for mixed case
        if (password.match(/[a-z]/) && password.match(/[A-Z]/)) { strength += 20; }

        return strength;
    };

    // Function to get the strength label and color
    const getStrengthLabelAndColor = (strength) => {
        if (strength < 40) {
            return { label: 'Weak', color: 'red' };
        } else if (strength < 70) {
            return { label: 'Medium', color: 'orange' };
        } else {
            return { label: 'Strong', color: 'green' };
        }
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setSecureTextEntry((prevState) => !prevState);
    };

    const onSubmitHandler = ({ values, resetForm, setSubmitting }) => {
        dispatch(showLoader());
        doPut('/password/reset', { ...otpInfo, password: values?.password }).then(response => {
            dispatch(hideLoader());
            setSubmitting(false);
            resetForm();
            dispatch(setOTPInfo(null));
            dispatch(showToast(response.data.message));
            navigation.replace('Login');
        }).catch(error => {
            errorHandler(error);
            setSubmitting(false);
        });
    };


    return (
        <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            <View style={styles.mainContainer}>
                <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                    <AggrLogo/>
                    <Formik
                        initialValues={{ password: '' }}
                        validationSchema={validationSchema}
                        onSubmit={(values, { resetForm, setSubmitting }) => {
                            console.log('Form submitted with:', values);
                            onSubmitHandler({ values, resetForm, setSubmitting });
                        }}
                    >
                        {({ handleChange, handleBlur, handleSubmit, isSubmitting, values, errors, touched }) => {
                            const strength = getPasswordStrength(values.password);
                            const { label, color } = getStrengthLabelAndColor(strength);
                            return (
                                <View>
                                    <View style={styles.inputContainer}>
                                        <Text variant="titleMedium">New Password</Text>
                                        <TextInput
                                            placeholder="Enter Password"
                                            mode="outlined"
                                            theme={{ roundness: InputRoundness }}
                                            style={styles.input}
                                            contentStyle={styles.contentStyle}
                                            secureTextEntry={secureTextEntry}
                                            value={values.password}
                                            onChangeText={handleChange('password')}
                                            onBlur={handleBlur('password')}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            maxLength={36}
                                            right={<TextInput.Icon color={theme.colors.primary} onPress={togglePasswordVisibility} size={24} icon={secureTextEntry ? 'eye-off' : 'eye'} />}

                                        />
                                        {touched.password && errors.password && (
                                            <Text style={styles.errorText}>{errors.password}</Text>
                                        )}
                                    </View>
                                    {/* Password Strength Bar (Only when password field is focused) */}
                                    {isPasswordFocused && values.password.length > 0 && (
                                        <View style={styles.passwordStrengthContainer}>
                                            <View
                                                style={[
                                                    styles.passwordStrengthBar,
                                                    { width: `${strength}%`, backgroundColor: color },
                                                ]}
                                            />
                                        </View>
                                    )}
                                    {isPasswordFocused && values.password.length > 0 && (
                                        <Text style={{ color, marginTop: 4, fontSize: 12 }}>
                                            Password Strength: {label}
                                        </Text>
                                    )}
                                    <View style={styles.inputContainer}>
                                        <Button
                                            theme={{ roundness: 36 }}
                                            onPress={handleSubmit}
                                            mode="contained"
                                            labelStyle={styles.labelStyle}
                                            disabled={isSubmitting}
                                        >
                                            Reset Password
                                        </Button>
                                    </View>
                                </View>
                            )
                        }}
                    </Formik>
                </ScrollView>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        // backgroundColor: theme.colors.primary,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
    },
    input: {
        fontSize: 14,
        backgroundColor: '#f6f8fa',
        marginVertical: 4,
    },
    contentStyle: {
        paddingLeft: 16,
    },
    inputContainer: {
        marginVertical: 8,
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 4,
    },
    passwordStrengthContainer: {
        marginTop: 8,
        height: 4,
        backgroundColor: '#e0e0e0',
        borderRadius: 5,
    },
    passwordStrengthBar: {
        height: '100%',
        borderRadius: 5,
    },
});

export default ResetPassword;
